import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Save, Trash2, Camera, Printer, FileDown } from "lucide-react";
import { showError } from "../lib/errorHandler";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHIFT = { morning: "Manhã", afternoon: "Tarde", night: "Noite" };
const ROLE_LABEL = { admin: "Admin", operator: "Operador", quality: "Qualidade", shift_leader: "Chefe" };

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a6fd91e7-1ad0-4cf0-91ce-b769f9bab27a/artifacts/m0y29wqk_Sem%20T%C3%ADtulo.png";

const fmtTime = (iso) => {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" }); }
  catch { return "-"; }
};

const fmtDate = (iso) => {
  if (!iso) return "-";
  try {
    // Se for "YYYY-MM-DD" ou ISO
    const d = iso.length === 10 ? new Date(iso + "T00:00:00") : new Date(iso);
    return d.toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric", weekday: "short" });
  } catch { return "-"; }
};

const fmtDuration = (minutes) => {
  const h = Math.floor((minutes || 0) / 60);
  const m = (minutes || 0) % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
};

export default function EmployeeProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [uRes, rRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/time-tracking/records?user_id=${userId}`),
      ]);
      const u = (uRes.data || []).find(x => x.id === userId);
      if (!u) { toast.error("Colaborador não encontrado"); return; }
      setUser(u);
      setForm({
        user_code: u.user_code || "",
        pin: "",
        name: u.name || "",
        role: u.role || "operator",
        shift: u.shift || "morning",
        function: u.function || "",
        photo_url: u.photo_url || "",
        admission_date: u.admission_date ? String(u.admission_date).slice(0, 10) : "",
        department: u.department || "",
        phone: u.phone || "",
        email: u.email || "",
        authorized_machines: u.authorized_machines || [],
      });
      setRecords(rRes.data || []);
    } catch (e) { showError(e, "Erro a carregar dados"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const onPhotoChange = (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Foto muito grande (max 2MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm(prev => ({ ...prev, photo_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      // pin field is required by backend model (str); send empty string to keep current
      const payload = { ...form, pin: form.pin || "" };
      await axios.put(`${API}/users/${userId}`, payload);
      toast.success("Perfil atualizado");
      load();
    } catch (e) { showError(e, "Erro a guardar"); }
    finally { setSaving(false); }
  };

  const deleteRecord = async (id) => {
    if (!window.confirm("Apagar este registo de picagem?")) return;
    try {
      await axios.delete(`${API}/time-tracking/records/${id}`);
      toast.success("Registo apagado");
      load();
    } catch (e) { showError(e, "Erro a apagar"); }
  };

  // Agrupar por mês (registros já vêm ordenados por date desc)
  const byMonth = records.reduce((acc, r) => {
    const key = (r.date || "").slice(0, 7); // YYYY-MM
    (acc[key] = acc[key] || []).push(r);
    return acc;
  }, {});
  const months = Object.keys(byMonth).sort().reverse();

  const monthTotal = (recs) => recs.reduce((sum, r) => sum + (r.total_minutes || 0), 0);
  const grandTotal = monthTotal(records);

  const openPDF = () => {
    const rowsHtml = months.map(month => {
      const recs = byMonth[month];
      const monthLabel = new Date(month + "-01").toLocaleDateString("pt-PT", { month: "long", year: "numeric" });
      const monthRows = recs.map(r => `
        <tr>
          <td class="mono">${fmtDate(r.date)}</td>
          <td class="mono" style="color:#059669">${fmtTime(r.clock_in)}</td>
          <td class="mono" style="color:#dc2626">${r.clock_out ? fmtTime(r.clock_out) : "<span style='color:#d97706'>em curso</span>"}</td>
          <td style="font-weight:bold">${fmtDuration(r.total_minutes)}</td>
          <td>${SHIFT[r.shift] || r.shift || "-"}</td>
          <td>${r.status === "active" ? "EM SERVIÇO" : "FECHADO"}</td>
        </tr>`).join("");
      return `
        <div class="month-block">
          <h3 style="text-transform:capitalize">${monthLabel} <span style="font-size:11px;color:#64748b">(${recs.length} dias · ${fmtDuration(monthTotal(recs))})</span></h3>
          <table>
            <thead><tr><th>Data</th><th>Entrada</th><th>Saída</th><th>Duração</th><th>Turno</th><th>Estado</th></tr></thead>
            <tbody>${monthRows}</tbody>
          </table>
        </div>`;
    }).join("");

    const html = `<!DOCTYPE html><html lang="pt"><head><meta charset="utf-8"><title>Registo ${form.name}</title>
<style>
  @page { size: A4; margin: 12mm; }
  body { font-family:'Segoe UI',Arial,sans-serif; color:#1e293b; margin:0; }
  .header { display:flex; justify-content:space-between; align-items:center; border-bottom:3px solid #1e3a8a; padding-bottom:10px; margin-bottom:14px; }
  .header img { height:55px; }
  .header h1 { margin:0; font-size:18px; color:#1e3a8a; }
  .id-card { background:linear-gradient(135deg,#1e3a8a,#3730a3); color:white; padding:14px; border-radius:8px; display:flex; gap:16px; align-items:center; margin-bottom:14px; }
  .id-card .avatar { width:80px; height:80px; border-radius:50%; background:rgba(255,255,255,0.2); display:flex; align-items:center; justify-content:center; font-size:28px; font-weight:bold; border:3px solid white; }
  .id-card .avatar img { width:100%; height:100%; border-radius:50%; object-fit:cover; }
  .id-card .info { flex:1; }
  .id-card .info .name { font-size:20px; font-weight:bold; }
  .id-card .info .role { font-size:11px; opacity:0.9; }
  .id-card .total { text-align:right; }
  .id-card .total .h { font-size:24px; font-weight:bold; color:#fbbf24; }
  .month-block { margin-bottom:16px; page-break-inside:avoid; }
  .month-block h3 { color:#1e3a8a; border-bottom:1px solid #e2e8f0; padding-bottom:4px; font-size:14px; margin:0 0 6px; }
  table { width:100%; border-collapse:collapse; font-size:10px; }
  thead { background:#f1f5f9; }
  thead th { padding:5px 6px; text-align:left; font-size:9px; text-transform:uppercase; color:#475569; border-bottom:2px solid #e2e8f0; }
  tbody td { padding:4px 6px; border-bottom:1px solid #e2e8f0; }
  .mono { font-family:monospace; }
  .footer { margin-top:12px; font-size:9px; color:#94a3b8; text-align:center; border-top:1px solid #e2e8f0; padding-top:6px; }
  .no-print { text-align:right; margin:10px 0; }
  @media print { .no-print { display:none; } }
</style></head><body>
<div class="no-print">
  <button onclick="window.print()" style="padding:8px 16px;background:#1e3a8a;color:white;border:none;border-radius:4px;font-size:13px;cursor:pointer">Imprimir / PDF</button>
</div>
<div class="header">
  <img src="${LOGO_URL}" alt="EP3D" />
  <div style="flex:1;text-align:center">
    <h1>Registo de Ponto Individual</h1>
    <div style="font-size:11px;color:#64748b">Gerado em ${new Date().toLocaleString("pt-PT")}</div>
  </div>
  <div style="text-align:right;font-size:10px;color:#64748b">
    <div>EP3D ENGINEERING</div><div>NIF: 517 316 188</div>
  </div>
</div>
<div class="id-card">
  <div class="avatar">${form.photo_url ? `<img src="${form.photo_url}" alt="" />` : (form.name || "?").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}</div>
  <div class="info">
    <div class="name">${form.name || "-"}</div>
    <div style="font-size:12px;opacity:0.9">${form.function || "-"} · ${form.department || "-"}</div>
    <div class="role" style="margin-top:6px">Código: <strong>${form.user_code}</strong> · Perfil: <strong>${ROLE_LABEL[form.role] || form.role}</strong> · Turno: <strong>${SHIFT[form.shift] || form.shift}</strong>${form.admission_date ? ` · Desde: <strong>${form.admission_date}</strong>` : ""}</div>
  </div>
  <div class="total">
    <div style="font-size:10px;opacity:0.85">Total</div>
    <div class="h">${fmtDuration(grandTotal)}</div>
    <div style="font-size:10px;opacity:0.85">${records.length} dias</div>
  </div>
</div>
${rowsHtml || "<p style='color:#94a3b8;text-align:center'>Sem picagens registadas.</p>"}
<div class="footer">Documento gerado automaticamente pelo Sistema MES EP3D. Reservado para uso interno.</div>
<script>setTimeout(() => window.print(), 800);</script>
</body></html>`;
    const w = window.open("", "_blank", "width=900,height=750");
    if (w) { w.document.write(html); w.document.close(); }
  };

  if (loading) return <div className="text-center py-20"><Loader2 className="h-8 w-8 animate-spin mx-auto" /></div>;
  if (!user) return null;

  return (
    <div className="p-6 space-y-5 max-w-6xl mx-auto">
      <div className="flex items-center justify-between print:hidden">
        <Button variant="ghost" onClick={() => navigate("/time-tracking")}><ArrowLeft className="h-4 w-4 mr-2" />Voltar</Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openPDF} className="border-red-500 text-red-700 hover:bg-red-50" data-testid="ep-pdf">
            <FileDown className="h-4 w-4 mr-2" />PDF
          </Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="ep-print"><Printer className="h-4 w-4 mr-2" />Imprimir</Button>
        </div>
      </div>

      {/* Cartão de identificação */}
      <Card className="overflow-hidden">
        <div className="bg-gradient-to-r from-blue-700 to-indigo-800 text-white p-6 flex items-center gap-6">
          <div className="relative">
            {form.photo_url ? (
              <img src={form.photo_url} alt={form.name} className="h-32 w-32 rounded-full object-cover border-4 border-white shadow-xl" />
            ) : (
              <div className="h-32 w-32 rounded-full bg-white/20 flex items-center justify-center text-4xl font-bold border-4 border-white shadow-xl">
                {(form.name || "?").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
              </div>
            )}
            <label className="absolute bottom-0 right-0 bg-yellow-400 hover:bg-yellow-500 text-slate-900 rounded-full p-2 cursor-pointer shadow-lg print:hidden" title="Mudar foto">
              <Camera className="h-4 w-4" />
              <input type="file" accept="image/*" className="hidden" onChange={onPhotoChange} data-testid="ep-photo-input" />
            </label>
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wider text-yellow-300 font-bold">EP3D · Colaborador</div>
            <div className="text-3xl font-bold mt-1">{form.name || "-"}</div>
            <div className="text-sm text-white/80 mt-1">
              {form.function || "-"} · {form.department || "-"}
            </div>
            <div className="flex gap-2 mt-3 text-xs flex-wrap">
              <span className="bg-white/20 px-2 py-1 rounded font-mono font-bold">{form.user_code}</span>
              <span className="bg-white/20 px-2 py-1 rounded">Turno: {SHIFT[form.shift] || form.shift}</span>
              <span className="bg-yellow-400 text-slate-900 px-2 py-1 rounded font-bold uppercase">{form.role}</span>
              {form.admission_date && <span className="bg-white/20 px-2 py-1 rounded">Desde {form.admission_date}</span>}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-yellow-300 uppercase">Total período</div>
            <div className="text-2xl font-bold">{fmtDuration(grandTotal)}</div>
            <div className="text-xs text-white/70">{records.length} dias registados</div>
          </div>
        </div>
      </Card>

      {/* Editar dados */}
      <Card className="print:hidden">
        <CardHeader><CardTitle className="text-base">Dados do Colaborador</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label>Código *</Label><Input value={form.user_code} disabled data-testid="ep-code" /></div>
            <div><Label>Nome completo *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="ep-name" /></div>
            <div><Label>Novo PIN (opcional)</Label><Input type="password" placeholder="deixe vazio para manter" value={form.pin} onChange={e => setForm({...form, pin: e.target.value})} data-testid="ep-pin" /></div>
            <div><Label>Função</Label><Input value={form.function} onChange={e => setForm({...form, function: e.target.value})} placeholder="Ex: Operador injeção" data-testid="ep-function" /></div>
            <div><Label>Departamento</Label><Input value={form.department} onChange={e => setForm({...form, department: e.target.value})} placeholder="Ex: Produção" /></div>
            <div><Label>Data de admissão</Label><Input type="date" value={form.admission_date || ""} onChange={e => setForm({...form, admission_date: e.target.value})} /></div>
            <div><Label>Telefone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
            <div>
              <Label>Turno</Label>
              <select className="flex h-10 w-full rounded-md border px-3 py-2 text-sm" value={form.shift} onChange={e => setForm({...form, shift: e.target.value})}>
                <option value="morning">Manhã</option>
                <option value="afternoon">Tarde</option>
                <option value="night">Noite</option>
              </select>
            </div>
            <div>
              <Label>Perfil</Label>
              <select className="flex h-10 w-full rounded-md border px-3 py-2 text-sm" value={form.role} onChange={e => setForm({...form, role: e.target.value})}>
                <option value="admin">Admin</option>
                <option value="operator">Operador</option>
                <option value="quality">Qualidade</option>
                <option value="shift_leader">Chefe de turno</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <Button onClick={save} disabled={saving} data-testid="ep-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Guardar Alterações
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Histórico picagens agrupadas por mês */}
      {months.length === 0 && (
        <Card><CardContent className="pt-6 text-center text-muted-foreground">Sem picagens registadas.</CardContent></Card>
      )}
      {months.map(month => {
        const recs = byMonth[month];
        const monthDate = new Date(month + "-01");
        const monthLabel = monthDate.toLocaleDateString("pt-PT", { month: "long", year: "numeric" });
        return (
          <Card key={month}>
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between">
                <span className="capitalize">{monthLabel}</span>
                <div className="flex gap-4 text-xs">
                  <span>Dias: <strong>{recs.length}</strong></span>
                  <span>Horas: <strong className="text-emerald-600">{fmtDuration(monthTotal(recs))}</strong></span>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Entrada</TableHead>
                    <TableHead>Saída</TableHead>
                    <TableHead>Duração</TableHead>
                    <TableHead>Turno</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right print:hidden">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recs.map(r => (
                    <TableRow key={r.id}>
                      <TableCell className="font-mono text-xs">{fmtDate(r.date)}</TableCell>
                      <TableCell className="font-mono text-xs text-green-700">🟢 {fmtTime(r.clock_in)}</TableCell>
                      <TableCell className="font-mono text-xs text-red-700">
                        {r.clock_out ? `🔴 ${fmtTime(r.clock_out)}` : <span className="text-amber-600">— em curso</span>}
                      </TableCell>
                      <TableCell className="font-semibold">{fmtDuration(r.total_minutes)}</TableCell>
                      <TableCell>{SHIFT[r.shift] || r.shift || "-"}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-[10px] font-bold ${r.status === "active" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-700"}`}>
                          {r.status === "active" ? "EM SERVIÇO" : "FECHADO"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right print:hidden">
                        <Button variant="ghost" size="icon" onClick={() => deleteRecord(r.id)} className="text-destructive" data-testid={`ep-del-${r.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
